import C from "classnames";
import * as React from "react";
import { CellProps, useExpanded, useFilters, useGlobalFilter, usePagination, useRowState, useSortBy, useTable, } from "react-table";
import { TableBody, TableHead } from ".";
import { Collapse } from "../../Utilities/Collapse";
import { CardFooter } from "../Card/CardFooter";
import { Dropdown } from "../Dropdown/Dropdown";
import { DropdownItem } from "../Dropdown/DropdownItem";
import { EditableTableCell } from "./EditableTableCell";
import { FilterColumnTableCell } from "./FilterColumnTableCell";
import { Table } from "./Table";
import { TableCell } from "./TableCell";
import { TableHeaderCell } from "./TableHeaderCell";
import { TableHeaderRow } from "./TableHeaderRow";
import { TablePagination } from "./TablePagination";
/*
  useGroupBy,
  useExpanded,
  
  */
import { TableRow } from "./TableRow";
const defaultColumn = {
    Cell: EditableTableCell,
    Filter: FilterColumnTableCell,
};
// used to toggle columns
class HiddenTableColumn {
}
const ToggleColumnsComponent = (props) => {
    const getLabel = (hc) => {
        if (hc.column && hc.column.label && hc.column.label.length > 0)
            return hc.column.label;
        const s = hc.name;
        const arr = s.split(".");
        if (!arr || arr.length === 1)
            return s;
        return arr[arr.length - 1];
    };
    const drawDropdownItems = () => {
        const items = [];
        props.hiddenCols.forEach((hc) => {
            const isHidden = hc.isHidden;
            items.push(React.createElement(DropdownItem, { key: hc.name, value: hc.name, onClick: () => {
                    props.onSelected(hc.name);
                } },
                React.createElement("div", null,
                    React.createElement("i", { style: isHidden ? { color: "white" } : {}, className: "fe fe-check mr-2" }),
                    getLabel(hc))));
        });
        return items;
    };
    return (React.createElement(Dropdown, { label: React.createElement("i", { className: "fe fe-pause small" }), variant: "info", className: "ml-1", dropAlign: "right" }, drawDropdownItems()));
};
export const DynamicTable = ({ className, flush, striped, bordered, hover, children, style, pagination = false, sortable = false, columns, data, defaultPageSize = 10, paginationRange = 5, hideColumns, containerType, unresponesive, onUpdateRow, onUpdateCell, onRefreshRow, ExpandedComponent, forceResetRowState, noHeader, forceResetFilters, }) => {
    const filterTypes = React.useMemo(() => ({
        startsWithText: (rows, id, filterValue) => {
            return rows.filter((row) => {
                const rowValue = row.values[id];
                return rowValue !== undefined
                    ? String(rowValue)
                        .toLowerCase()
                        .startsWith(String(filterValue).toLowerCase())
                    : true;
            });
        },
        includesText: (rows, id, filterValue) => {
            return rows.filter((row) => {
                const rowValue = row.values[id];
                return rowValue !== undefined
                    ? String(rowValue)
                        .toLowerCase()
                        .includes(String(filterValue).toLowerCase())
                    : true;
            });
        },
    }), []);
    const [searchParams, setSearchParams] = React.useState(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const paramObj = Object.fromEntries(urlSearchParams.entries());
        const params = Object.keys(paramObj).map((paramKey) => ({
            id: paramKey,
            value: paramObj[paramKey],
        }));
        return params;
    });
    const [loadingParams, setLoadingParams] = React.useState(true);
    const [extraColumns, setExtraColumns] = React.useState([]); // toggling hidden-visible
    const getHiddenColNames = (hidden_cols) => {
        const items = [];
        hidden_cols.forEach((hc) => {
            if (hc.isHidden)
                items.push(hc.name);
        });
        return items;
    };
    const getColumnByAccessor = (accessor) => {
        for (let i = 0; i < columns.length; i++) {
            const c = columns[i];
            if (c.accessor === accessor)
                return columns[i];
        }
        return null;
    };
    React.useEffect(() => {
        if (!hideColumns)
            return;
        if (extraColumns.length === 0) {
            const hc = [];
            hideColumns.forEach((line) => {
                hc.push({
                    name: line,
                    isHidden: true,
                    column: getColumnByAccessor(line),
                });
            });
            setExtraColumns(hc);
        }
    }, [hideColumns]);
    let tableInstance = useTable({
        columns,
        data,
        defaultColumn,
        filterTypes,
        initialState: {
            pageIndex: 0,
            hiddenColumns: hideColumns ? hideColumns : [],
            filters: searchParams || [],
        },
        onUpdateRow,
        onRefreshRow,
        onUpdateCell,
        autoResetRowState: forceResetRowState,
        autoResetExpanded: false,
        autoResetFilters: !!forceResetFilters,
    }, useGlobalFilter, useFilters, useSortBy, useRowState, useExpanded, usePagination);
    const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, preGlobalFilteredRows, setGlobalFilter, visibleColumns, state: { expanded }, } = tableInstance;
    const state = tableInstance.state;
    React.useEffect(() => {
        tableInstance.setPageSize(defaultPageSize);
    }, [tableInstance.setPageSize]);
    // [Begin] fix for: table jumps to the first page
    const [currentPage, setCurrentPage] = React.useState(0);
    const onGoToPage = (n) => {
        setCurrentPage(n);
    };
    const onNextPage = () => {
        setCurrentPage(currentPage + 1);
    };
    const onPrevPage = () => {
        setCurrentPage(currentPage - 1);
    };
    React.useEffect(() => {
        tableInstance.gotoPage(currentPage);
    }, [currentPage]);
    React.useEffect(() => {
        tableInstance.gotoPage(currentPage);
    }, [page]);
    // [End] fix for: table jumps to the first page
    const ConditionalWrapper = ({ condition, wrapper, children }) => condition ? wrapper(children) : children;
    return (React.createElement("div", { className: C(!unresponesive && "table-responsive") },
        React.createElement(Table, Object.assign({ className: C("table", !flush && "card-table", className, striped && "table-striped", bordered && "table-bordered", hover && "table-hover"), style: style }, getTableProps()),
            !noHeader && (React.createElement(TableHead, null, headerGroups.map((headerGroup) => (React.createElement(TableHeaderRow, Object.assign({}, headerGroup.getHeaderGroupProps()), headerGroup.headers.map((column, i) => {
                return column.Header ? (sortable ? (React.createElement(TableHeaderCell, { key: column.id },
                    React.createElement("div", { className: "d-flex flex-row align-items-end" },
                        React.createElement("div", null,
                            React.createElement("div", Object.assign({}, column.getHeaderProps(column.getSortByToggleProps()), { className: C("p-1", column.isSorted
                                    ? column.isSortedDesc
                                        ? "sort-desc"
                                        : "sort-asc"
                                    : "") }), column.render("Header")),
                            React.createElement("div", { className: "" }, column.canFilter
                                ? column.render("Filter")
                                : null)),
                        hideColumns &&
                            hideColumns.length > 0 &&
                            i === headerGroup.headers.length - 1 && (React.createElement("div", null,
                            React.createElement(ToggleColumnsComponent, { hiddenCols: extraColumns, onSelected: (col) => {
                                    const res = extraColumns.find((item) => item.name === col);
                                    if (res) {
                                        // toggle isHidden
                                        const filtered = extraColumns.filter((item) => item.name !== col);
                                        res.isHidden = !res.isHidden;
                                        setExtraColumns([res, ...filtered]); // array of column objects
                                        tableInstance.setHiddenColumns(getHiddenColNames([res, ...filtered])); // array of strings
                                    }
                                } })))))) : (React.createElement(TableHeaderCell, { key: column.id },
                    React.createElement("div", { className: "p-1" }, column.render("Header")),
                    React.createElement("div", { className: "" }, column.canFilter ? column.render("Filter") : null)))) : (React.createElement(TableHeaderCell, { className: "p-0", key: column.id }));
            })))))),
            React.createElement(TableBody, Object.assign({}, getTableBodyProps()),
                page.map((row, i) => {
                    prepareRow(row);
                    //console.log(row);
                    return (React.createElement(React.Fragment, { key: i },
                        React.createElement(TableRow, Object.assign({}, row.getRowProps(), { key: row.index, className: row.original.rowClassNames }), row.cells.map((cell, i) => {
                            var _a, _b, _c, _d, _e, _f;
                            return (React.createElement(TableCell, Object.assign({ key: row.index + row.id }, cell.getCellProps()), !((_b = (_a = row === null || row === void 0 ? void 0 : row.original) === null || _a === void 0 ? void 0 : _a.hideCells) === null || _b === void 0 ? void 0 : _b.includes((_c = cell === null || cell === void 0 ? void 0 : cell.column) === null || _c === void 0 ? void 0 : _c.id)) &&
                                cell.render("Cell", {
                                    editable: !((_e = (_d = row === null || row === void 0 ? void 0 : row.original) === null || _d === void 0 ? void 0 : _d.disableCellsEdit) === null || _e === void 0 ? void 0 : _e.includes((_f = cell === null || cell === void 0 ? void 0 : cell.column) === null || _f === void 0 ? void 0 : _f.id)) && row.state.editable,
                                    updated: row.state.updated,
                                })));
                        })),
                        ExpandedComponent && !row.original.subRows && (React.createElement(TableRow, { className: C(row.original.contentRowClassNames) },
                            React.createElement(TableCell, { colSpan: visibleColumns.length, className: C(!row.isExpanded && "p-0  ", "border-top-0 border-bottom border-white"), style: { transition: "padding 150ms" } },
                                React.createElement(Collapse, { isOpen: row.isExpanded, timeout: 150, appear: true, unmountOnExit: true, mountOnEnter: true }, React.cloneElement(ExpandedComponent, {
                                    expandedBookData: row.original.expandedBookData,
                                    expandedUsersData: row.original.expandedUsersData,
                                    expandedOrderData: row.original.expandedOrderData,
                                    orderedItem: row.original,
                                })))))));
                }),
                children)),
        React.createElement(ConditionalWrapper, { condition: containerType && containerType === "card", wrapper: (children) => React.createElement(CardFooter, null, children) }, pagination ? (React.createElement(TablePagination, { tableState: { pageIndex: currentPage, pageSize: 0 }, gotoPage: onGoToPage, nextPage: onNextPage, previousPage: onPrevPage, canNextPage: tableInstance.canNextPage, canPreviousPage: tableInstance.canPreviousPage, paginationRange: paginationRange, pageCount: tableInstance.pageCount })) : (""))));
};
export { CellProps };
//# sourceMappingURL=DynamicTable.js.map